/**
 * How to use the application.
 *
 * Written against what the code actually does rather than what a feature list
 * would say: the follow-up delays are the real ones from FOLLOWUP_DELAYS, the
 * outcomes that retire a candidate are the real DESTRUCTIVE_CALL_OUTCOMES, and
 * the statuses named are the real enum members. A help page that describes a
 * product slightly different from the one on screen is worse than none,
 * because somebody will trust it.
 *
 * Sections are gated by role for the same reason the navigation is. An
 * employee has no pipeline, and a page that explains submissions to somebody
 * who cannot see them is twenty screens of noise around the four that apply.
 * Nothing here is a permission — every section describes a page the role can
 * already open.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeIndianRupee,
  Bell,
  Building2,
  ChevronDown,
  ClipboardList,
  LifeBuoy,
  Phone,
  ShieldCheck,
  Timer,
  Users,
} from 'lucide-react';
import {
  isCompanyAdmin,
  isStaff,
  isSupervisor,
  canSeeAllCompanies,
  useAuth,
} from '../store/auth';

/* ------------------------------------------------------------------ */
/* Presentation                                                        */
/* ------------------------------------------------------------------ */

/**
 * One collapsible chapter.
 *
 * Open by default. A help page that starts as a list of closed headings makes
 * somebody click before they can tell whether their answer is here, and the
 * whole point is that they are already stuck.
 */
function Chapter({
  id,
  title,
  icon: Icon,
  children,
}: {
  id: string;
  title: string;
  icon: typeof Phone;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <section
      id={id}
      className="scroll-mt-4 rounded-xl bg-white shadow-sm ring-1 ring-gray-200
                 transition-shadow duration-300 ease-out hover:shadow-md"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left
                   transition-colors duration-300 ease-out hover:bg-gray-50"
      >
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg
                     bg-indigo-50 text-indigo-600"
        >
          <Icon className="h-4.5 w-4.5" />
        </span>
        <h2 className="flex-1 text-sm font-semibold text-gray-900">{title}</h2>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300
                      ease-out ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {open && (
        <div className="space-y-4 border-t border-gray-100 px-4 py-4 text-sm leading-relaxed text-gray-700">
          {children}
        </div>
      )}
    </section>
  );
}

/** A labelled step or term. */
function Item({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-medium text-gray-900">{term}</p>
      <p className="mt-0.5 text-gray-600">{children}</p>
    </div>
  );
}

/** A row of a small reference table. */
function Row({ left, right }: { left: string; right: string }) {
  return (
    <tr className="border-t border-gray-100">
      <td className="py-1.5 pr-4 align-top font-medium text-gray-900">{left}</td>
      <td className="py-1.5 align-top text-gray-600">{right}</td>
    </tr>
  );
}

function Table({
  head,
  children,
}: {
  head: [string, string];
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[22rem] text-left text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-gray-400">
            <th className="pb-1 pr-4 font-semibold">{head[0]}</th>
            <th className="pb-1 font-semibold">{head[1]}</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** Something that will bite if it is not said out loud. */
function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
      {children}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export default function HelpPage() {
  const user = useAuth((s) => s.user);
  const staff = isStaff(user);
  const supervisor = isSupervisor(user);
  const admin = isCompanyAdmin(user);
  const owner = canSeeAllCompanies(user);

  const contents = [
    { id: 'day', label: 'Starting your day' },
    { id: 'myday', label: 'Reading the dashboard' },
    ...(staff ? [{ id: 'pipeline', label: 'The recruitment pipeline' }] : []),
    ...(staff ? [{ id: 'calls', label: 'Logging a call' }] : []),
    ...(staff ? [{ id: 'incentives', label: 'How incentives work' }] : []),
    { id: 'mywork', label: 'Your own records' },
    ...(supervisor ? [{ id: 'managers', label: 'For managers' }] : []),
    ...(admin ? [{ id: 'admins', label: 'For administrators' }] : []),
    ...(owner ? [{ id: 'owner', label: 'Working across companies' }] : []),
    { id: 'stuck', label: 'When something looks wrong' },
  ];

  return (
    <div className="space-y-5">
      <header>
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-5 w-5 text-indigo-600" />
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">Help</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          How the application works, and what each screen expects of you.
        </p>
      </header>

      {/* Contents. Anchors rather than a router, so a link lands on the
          chapter without discarding the ones already open. */}
      <nav className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          On this page
        </p>
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
          {contents.map(({ id, label }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="text-sm text-indigo-600 underline-offset-2 transition-colors
                           duration-300 ease-out hover:text-indigo-800 hover:underline"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* --- Starting your day ----------------------------------------- */}
      <Chapter id="day" title="Starting your day" icon={Timer}>
        <Item term="Start your shift">
          Nothing else records your time. The button sits at the top of the
          dashboard, and until you press it the day counts as not started —
          calls you log will save, but your attendance will not.
        </Item>
        <Item term="Location consent comes first">
          You are asked once, before your first shift, rather than in the middle
          of a timed prompt. You can decline; the shift still runs.
        </Item>
        <Item term="Breaks and meetings">
          Mark a break when you step away, and a meeting when you are in one.
          Meeting types are client, team, lunch, morning and evening. Time
          inside a marked break or meeting is treated differently from time
          simply unaccounted for.
        </Item>
        <Item term="Check-ins">
          The server decides when to ask, and a prompt takes over the screen
          until you answer. The answers are working, meeting, field visit,
          break, commuting and idle. Answer honestly — an unanswered check-in
          is not neutral, it counts against the hours for that stretch.
        </Item>
        <Item term="End your shift">
          Press it before you leave. A shift left open does not quietly close
          itself at midnight.
        </Item>
        <Note>
          Check-ins only reach a tab that is open. If you work with the
          application in a background tab all afternoon you will still be
          prompted, but keep it open — a missed prompt is a discounted stretch.
        </Note>
      </Chapter>

      {/* --- Reading the dashboard ------------------------------------- */}
      <Chapter id="myday" title="Reading the dashboard" icon={ClipboardList}>
        <p>
          The dashboard is the page you land on. It answers one question: what
          is waiting on you right now.
        </p>
        <Table head={['Tile', 'What it counts']}>
          <Row
            left="Recall"
            right="Chases you promised and are now past their date. This is the one to clear first."
          />
          <Row left="Due today" right="Chases falling due today, not yet overdue." />
          <Row left="Calls today" right="Calls you have logged since midnight." />
          <Row
            left="Submissions today"
            right="Candidates you have put forward to a client today."
          />
        </Table>
        <Item term="The Recall table">
          Correcting a row here is the same edit as correcting it on the Daily
          Calls Report — one record read in two places, not two copies. Use
          Follow up to log the next call, or the pencil to fix what the last one
          said.
        </Item>
        <Item term="The rail on the left">
          Only the dashboard has it. Every other page keeps the tab bar across
          the top, which is deliberate: the wide tables need the width the rail
          would take.
        </Item>
      </Chapter>

      {/* --- The pipeline ---------------------------------------------- */}
      {staff && (
        <Chapter id="pipeline" title="The recruitment pipeline" icon={Users}>
          <p>
            A candidate moves through the same sequence every time. Each screen
            owns one stretch of it.
          </p>
          <Item term="1. Clients">
            Who you are hiring for, and the agreed fee. Open a client to see its
            requirements. <Link className="text-indigo-600 hover:underline" to="/clients">Clients</Link>
          </Item>
          <Item term="2. Interview Candidates">
            The working list of people. Add someone, record what you know, and
            submit them to a client when they are ready.{' '}
            <Link className="text-indigo-600 hover:underline" to="/candidates">
              Interview Candidates
            </Link>
          </Item>
          <Item term="3. Daily Calls Report">
            Every dial, with its outcome. This is where the day's work is
            actually recorded.{' '}
            <Link className="text-indigo-600 hover:underline" to="/calls">
              Daily Calls Report
            </Link>
          </Item>
          <Item term="4. Interviews and Offers">
            Scheduled interviews, and offers once extended. An offer is
            extended, then accepted, declined or withdrawn.
          </Item>
          <Item term="5. Placements and the Joining Tracker">
            A placement is made when somebody accepts. The Joining Tracker is
            the same placements read in the client's sheet order — it is a view,
            not a second record.{' '}
            <Link className="text-indigo-600 hover:underline" to="/tracker">
              Joining Tracker
            </Link>
          </Item>
          <p className="pt-1 font-medium text-gray-900">Where a candidate can sit</p>
          <p className="text-gray-600">
            A submission reads as pending, shortlisted, selected, rejected,
            hold, followup, yet to join, or working. A placement then carries
            yet to join, working, drop, not joined, rejected, hold or
            replacement done. The tracker shows the fuller sequence, including
            interview scheduled, interviewed, offered, offer declined and offer
            withdrawn.
          </p>
          <Note>
            Yet to join and working are the two that matter for money. A
            placement that never becomes working earns nothing, and the
            incentive reverses.
          </Note>
        </Chapter>
      )}

      {/* --- Calls ------------------------------------------------------ */}
      {staff && (
        <Chapter id="calls" title="Logging a call" icon={Phone}>
          <p>
            Pick the outcome that describes what happened. It is not
            bookkeeping: the outcome decides whether the candidate comes back to
            you, and when.
          </p>
          <p className="pt-1 font-medium text-gray-900">
            Outcomes that schedule a chase
          </p>
          <Table head={['Outcome', 'Comes back in']}>
            <Row left="Busy" right="4 hours" />
            <Row left="Disconnected" right="4 hours" />
            <Row left="Call back" right="1 day" />
            <Row left="Interested" right="1 day" />
            <Row left="Sent WhatsApp" right="1 day" />
            <Row left="Info sent" right="2 days" />
            <Row left="Awaiting confirmation" right="2 days" />
            <Row left="Yet to get resume" right="2 days" />
            <Row left="Hold" right="3 days" />
          </Table>
          <p className="text-gray-600">
            Everything else ends the thread and creates no reminder. Reminders
            land at 10:00 rather than at whatever hour you made the call — a
            chase scheduled for 21:47 is a chase nobody sees.
          </p>
          <p className="pt-1 font-medium text-gray-900">
            Outcomes that retire the candidate
          </p>
          <p className="text-gray-600">
            Wrong profile, not suitable, invalid number, out of service, and
            too many tried. The person leaves your working lists, the count of
            those still to call, and every picker. Nothing is deleted — the
            calls and the history stay, and an administrator can restore them.
          </p>
          <Note>
            Not picking and switched off deliberately do <em>not</em> schedule a
            chase. A worklist that resurfaces every unanswered dial is one
            nobody can work through.
          </Note>
        </Chapter>
      )}

      {/* --- Incentives ------------------------------------------------- */}
      {staff && (
        <Chapter id="incentives" title="How incentives work" icon={BadgeIndianRupee}>
          <p>
            Four percent of the agreed fee on your placements. The dashboard
            shows three figures and they mean different things.
          </p>
          <Table head={['Figure', 'What it means']}>
            <Row
              left="Tentative Incentive"
              right="Provisional. The candidate has been placed but has not yet completed ninety days. It is not owed to you yet and can reverse."
            />
            <Row
              left="Earned Incentive"
              right="The ninety days are done. This is yours."
            />
            <Row
              left="Yet to receive"
              right="Earned but not yet paid out."
            />
          </Table>
          <Item term="When it becomes earned">
            Ninety days after the candidate joins, paid with the following
            month's salary.
          </Item>
          <Item term="When it reverses">
            If the candidate never joins, drops before the ninety days, or the
            fee is cancelled. A reversal is a new entry, never an edit to the
            old one, so the history stays readable.
          </Item>
          <Note>
            Tentative is not a promise. Read it as work in progress, and expect
            the figure to move.
          </Note>
        </Chapter>
      )}

      {/* --- My Work ---------------------------------------------------- */}
      <Chapter id="mywork" title="Your own records" icon={ClipboardList}>
        <Item term="Attendance">
          What your shifts and check-ins added up to. A day reads as present,
          half day, absent, leave, holiday, weekly off, or unmonitored.
        </Item>
        <Item term="Leave">
          File your own request and watch it move from pending to approved,
          rejected or cancelled. Types are casual, sick, earned, unpaid and
          comp off. The holiday list is here too.
        </Item>
        <Item term="Timesheets">
          Your week. A timesheet is a draft until you submit it, and then waits
          for approval. A rejected one comes back for correction.
        </Item>
        <Item term="Payslips">Issued payslips, to read or download.</Item>
        <Item term="My Profile">
          Your own details and documents. Changes to sensitive fields go to a
          reviewer rather than saving straight away — draft, then pending, then
          approved or rejected.
        </Item>
      </Chapter>

      {/* --- Managers --------------------------------------------------- */}
      {supervisor && (
        <Chapter id="managers" title="For managers" icon={Bell}>
          <Item term="Team">
            Your reports and what their day looked like.
          </Item>
          <Item term="Attendance records">
            The same attendance across the people who report to you, for
            checking rather than filing.
          </Item>
          <Item term="Approvals">
            Leave and timesheets arrive as pending and stay there until somebody
            acts. Nothing approves itself with time.
          </Item>
          <Note>
            You see what reports to you, not the whole company. If a row you
            expect is missing, the reporting line is usually the reason.
          </Note>
        </Chapter>
      )}

      {/* --- Administrators --------------------------------------------- */}
      {admin && (
        <Chapter id="admins" title="For administrators" icon={ShieldCheck}>
          <Item term="Users">
            Accounts, roles and reporting lines. A role change applies the next
            time that person's session refreshes, not at their next login.
          </Item>
          <Item term="Profile Reviews">
            Where submitted profile changes wait. Reviewing one means seeing
            full bank, PAN and Aadhaar details, which is why it is
            administrator-only rather than manager-only.
          </Item>
          <Item term="Retired candidates">
            The other side of a destructive call outcome. Nothing was deleted,
            so anybody retired can be put back — an explicit, audited act.
          </Item>
          <Item term="Billing, Exports, Payroll">
            Invoices and their payment state, data extracts, and pay runs with
            their setup.
          </Item>
          <Item term="Audit trail">
            Who changed what. The first place to look when a record does not say
            what somebody expects.
          </Item>
          <Item term="Custom fields">
            Extra fields on candidates and clients. Closer to a schema change
            than a setting — add sparingly.
          </Item>
        </Chapter>
      )}

      {/* --- Owner ------------------------------------------------------ */}
      {owner && (
        <Chapter id="owner" title="Working across companies" icon={Building2}>
          <Item term="Pick a company first">
            You belong to no single company, so most screens cannot guess which
            one you mean and will ask before they let you create anything.
          </Item>
          <Item term="All companies">
            An explicit choice, never the default. Counts and totals then span
            every tenant at once, and an amber banner says so. Nothing can be
            created in that mode — a record has to belong to one company.
          </Item>
          <Item term="Switching">
            Changing company clears what was loaded for the last one, so the
            screen reloads rather than showing you the previous tenant's rows.
          </Item>
        </Chapter>
      )}

      {/* --- Troubleshooting -------------------------------------------- */}
      <Chapter id="stuck" title="When something looks wrong" icon={LifeBuoy}>
        <Item term="A page says to choose a company">
          You have not picked one yet. Nothing is broken.
        </Item>
        <Item term="Numbers look far too large">
          Check whether you are reading every company at once. The amber banner
          at the top of the page is the tell.
        </Item>
        <Item term="A candidate has vanished">
          A destructive call outcome retires them. They are on the retired list
          and an administrator can restore them.
        </Item>
        <Item term="A chase never appeared">
          Not every outcome schedules one. Check the table above — not picking
          and switched off are the two people most often expect to.
        </Item>
        <Item term="Your hours look short">
          Unanswered check-ins are the usual cause. An open shift that was never
          ended is the other.
        </Item>
        <Item term="A link you used to have is gone">
          Roles decide what appears. If your role changed recently, the
          navigation changed with it.
        </Item>
        <Note>
          If something still looks wrong, note the screen, the record and the
          time, and tell your administrator — the audit trail can be read back
          from those three things.
        </Note>
      </Chapter>
    </div>
  );
}
